/**
 * CoPilotGuide Wrapper Component
 *
 * Provides a reusable React interface for the CoPilotGuideEngine.
 * Handles flow state, transitions, and renders chat widgets.
 */

import {
  CoPilotGuideEngine,
  type Chat,
  type CoPilotGuideConfig,
  type Task,
  type TransitionResult,
} from '@tonnie/copilot';
import { useTranslation } from '@tonnie/locale';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// ═══════════════════════════════════════════════════════════════════════
// CONTEXT TYPES
// ═══════════════════════════════════════════════════════════════════════

interface ICoPilotGuideContextValue {
  // Current state
  currentChat: Chat | null;
  currentTask: Task | null;
  isStarted: boolean;
  isCompleted: boolean;
  variables: Record<string, unknown>;

  // Actions
  start: () => void;
  transition: (transitionId: string, effects?: string[]) => TransitionResult;
  setVariable: (key: string, value: unknown) => void;
  reset: () => void;

  // History
  chatHistory: Chat[];
  progress: number;
}

const CoPilotGuideContext = createContext<ICoPilotGuideContextValue | null>(null);

// ═══════════════════════════════════════════════════════════════════════
// PROVIDER COMPONENT
// ═══════════════════════════════════════════════════════════════════════

interface ICoPilotGuideProviderProps {
  config: CoPilotGuideConfig;
  onComplete?: (variables: Record<string, unknown>) => void;
  onTransition?: (result: TransitionResult) => void;
  children: ReactNode;
}

export function CoPilotGuideProvider({
  config,
  onComplete,
  onTransition,
  children,
}: ICoPilotGuideProviderProps) {
  const [engine] = useState(() => new CoPilotGuideEngine(config));
  const [currentChat, setCurrentChat] = useState<Chat | null>(null);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);
  const [chatHistory, setChatHistory] = useState<Chat[]>([]);
  const [variables, setVariables] = useState<Record<string, unknown>>({});
  const [isStarted, setIsStarted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // Calculate progress
  const progress = useMemo(() => {
    const totalChats = config.content.tasks.reduce((sum, task) => sum + task.chats.length, 0);
    if (totalChats === 0) return 0;
    return Math.round((chatHistory.length / totalChats) * 100);
  }, [config.content.tasks, chatHistory.length]);

  // Start the guide
  const start = useCallback(() => {
    const firstChat = engine.start();
    if (firstChat) {
      setCurrentChat(firstChat);
      setCurrentTask(engine.getCurrentTask());
      setIsStarted(true);
      setChatHistory([firstChat]);
    }
  }, [engine]);

  // Handle transitions
  const transition = useCallback(
    (transitionId: string, effects?: string[]): TransitionResult => {
      const result = engine.transition(transitionId, effects);

      // Update current chat
      const newChat = engine.getCurrentChat();
      setCurrentChat(newChat);
      setCurrentTask(engine.getCurrentTask());

      // Add to history if new chat
      if (newChat && !chatHistory.some((c) => c.id === newChat.id)) {
        setChatHistory((prev) => [...prev, newChat]);
      }

      // Handle completion
      if (result.shouldClose) {
        setIsCompleted(true);
        onComplete?.(variables);
      }

      // Callback
      onTransition?.(result);

      return result;
    },
    [engine, chatHistory, variables, onComplete, onTransition]
  );

  // Set a variable
  const setVariable = useCallback((key: string, value: unknown) => {
    setVariables((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Reset the guide
  const reset = useCallback(() => {
    setCurrentChat(null);
    setCurrentTask(null);
    setChatHistory([]);
    setVariables({});
    setIsStarted(false);
    setIsCompleted(false);
  }, []);

  const value = useMemo(
    () => ({
      currentChat,
      currentTask,
      isStarted,
      isCompleted,
      variables,
      start,
      transition,
      setVariable,
      reset,
      chatHistory,
      progress,
    }),
    [
      currentChat,
      currentTask,
      isStarted,
      isCompleted,
      variables,
      start,
      transition,
      setVariable,
      reset,
      chatHistory,
      progress,
    ]
  );

  return <CoPilotGuideContext.Provider value={value}>{children}</CoPilotGuideContext.Provider>;
}

// ═══════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════

export function useCoPilotGuide(): ICoPilotGuideContextValue {
  const context = useContext(CoPilotGuideContext);
  if (!context) {
    throw new Error('useCoPilotGuide must be used within a CoPilotGuideProvider');
  }
  return context;
}

// ═══════════════════════════════════════════════════════════════════════
// SIMPLE CHAT DISPLAY COMPONENT
// ═══════════════════════════════════════════════════════════════════════

interface ICoPilotGuideChatProps {
  className?: string;
}

export function CoPilotGuideChat({ className = '' }: ICoPilotGuideChatProps) {
  const { t } = useTranslation();
  const { currentChat, transition, isStarted, start, isCompleted, progress } = useCoPilotGuide();

  // Auto-start if not started
  useEffect(() => {
    if (!isStarted) {
      start();
    }
  }, [isStarted, start]);

  if (isCompleted) {
    return (
      <div className={`text-center p-6 ${className}`}>
        <div className="text-4xl mb-4">🎉</div>
        <h3 className="text-lg font-semibold mb-2">{t('copilot.guideCompleted')}</h3>
        <p className="text-muted-foreground">{t('copilot.guideCompletedDesc')}</p>
      </div>
    );
  }

  if (!currentChat) {
    return (
      <div className={`text-center p-6 ${className}`}>
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className={className} data-testid="copilot-guide-wrapper">
      {/* Progress indicator */}
      <div className="mb-4">
        <div className="h-1 bg-border rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {t('copilot.progressComplete', { progress })}
        </p>
      </div>

      {/* Chat content */}
      <div className="space-y-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="text-sm">{currentChat.messages?.[0]?.text ?? ''}</p>
        </div>

        {/* Actions */}
        {currentChat.actions && currentChat.actions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {currentChat.actions.map((action) => (
              <button
                key={action.id}
                onClick={() => transition(action.transition ?? '', action.effects)}
                className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {action.text}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
